import React, { useEffect, useState, useRef } from "react";
import ContactDetails from "../ContactDetails/ContactDetails";
import InsightPanel from "../InsightPanel/InsightPanel";
import ActionPanel from "../ActionPanel/ActionPanel";
import "./DetailsDashboard.css";
import { useDispatch, useSelector } from "react-redux";
import { getDetailsPageDataInit } from "../../../redux/slices/operationHomeSlice";
import { useLocation } from "react-router-dom";

const Dashboard = ({ memberId, callId, onCallChange, navigationDisabled = false }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const actionPanelRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [groupedCalls, setGroupedCalls] = useState(null);
  const [showActionPanel, setShowActionPanel] = useState(false);
  const [currentCallId, setCurrentCallId] = useState(callId);

  const memberDetails = useSelector(
    (state) => state.operationHome.details.data,
  );
  const size = useSelector((state) => state.operationHome.details.size);

  // Check if navigated from notification and open ActionPanel
  useEffect(() => {
    if (location.state?.openActionPanel) {
      setShowActionPanel(true);
      // Scroll to ActionPanel after a short delay to ensure DOM is ready
      setTimeout(() => {
        if (actionPanelRef.current) {
          actionPanelRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 300);
    }
  }, [location.state?.openActionPanel]);

  useEffect(() => {
    dispatch(getDetailsPageDataInit(memberId));
  }, [dispatch, memberId]);

  useEffect(() => {
    if (!memberDetails?.callDetails) return;

    // Group by CallId to keep all prompt/response entries together
    const callsByCallId = memberDetails.callDetails.reduce((acc, current) => {
      const callId = current.CallId;
      if (!acc[callId]) {
        acc[callId] = {
          CallId: callId,
          meetingDate: current.meetingDate,
          meetingPerson: current.meetingPerson,
          callCategory: current.callCategory,
          callType: current.callType,
          duration: current.duration,
          status: current.status,
          rating: current.rating,
          items: [], // This will hold all prompt/response entries for this call
        };
      }
      // Add the current item to the call's items array
      acc[callId].items.push(current);
      return acc;
    }, {});

    // Convert the grouped object to an array and sort by meeting date
    const groupedArray = Object.values(callsByCallId).sort((a, b) => {
      if (a.meetingDate < b.meetingDate) return -1;
      if (a.meetingDate > b.meetingDate) return 1;
      return 0;
    });

    setGroupedCalls(groupedArray);

    // Find the index of the current callId
    if (callId && groupedArray.length > 0) {
      const index = groupedArray.findIndex((call) => call.CallId === callId);
      if (index !== -1) {
        setCurrentIndex(index);
        setCurrentCallId(callId);
        onCallChange?.(callId);
      }
    }
  }, [memberDetails, callId]);

  const currentCall = groupedCalls?.[currentIndex];

  // Prepare section object for ContactDetails
  const sectionForContact = currentCall
    ? {
        meetingDate: currentCall.meetingDate,
        meetingPerson: currentCall.meetingPerson,
        items: currentCall.items || [], // Pass all items for this call
      }
    : {};

  const currentStatus = currentCall?.status?.toLowerCase();

  const shouldRenderActionPanel = [
    "yes",
    "in-progress",
    "closed",
    "no",
  ].includes(currentStatus);

  // Handle section change from ContactDetails arrows — ignored while a PDF
  // download is in flight (see DetailsScreen.js) so switching calls mid-
  // generation can't redirect the download to the wrong call's content.
  const handleSectionChange = (newIndexOrUpdater) => {
    if (navigationDisabled) return;

    // Resolve to a numeric index
    const newIndex =
      typeof newIndexOrUpdater === "function"
        ? newIndexOrUpdater(currentIndex) // call it with currentIndex
        : newIndexOrUpdater;

    // console.log("New index:", newIndex);
    setCurrentIndex(newIndex);

    if (groupedCalls?.[newIndex]?.CallId) {
      // console.log("Updating currentCallId to:", groupedCalls[newIndex].CallId);
      setCurrentCallId(groupedCalls[newIndex].CallId);
      onCallChange?.(groupedCalls[newIndex].CallId);
    }
  };

  return (
    <div
      className={`dashboard-grid ${
        shouldRenderActionPanel
          ? showActionPanel
            ? "with-action-panel"
            : "collapsed-action-panel"
          : "no-action-panel"
      }`}
    >
      <ContactDetails
        memberId={memberId}
        memberDetails={memberDetails}
        section={sectionForContact}
        sectionLength={groupedCalls?.length}
        setSection={handleSectionChange}
        currentSection={currentIndex}
        callId={currentCallId}
        size={size}
        navigationDisabled={navigationDisabled}
      />

      <InsightPanel
        memberId={memberId}
        memberDetails={memberDetails}
        section={sectionForContact}
        callId={currentCallId}
        shouldRenderActionPanel={shouldRenderActionPanel}
      />

      {shouldRenderActionPanel && (
        <div ref={actionPanelRef}>
          <ActionPanel
            memberId={memberId}
            callId={currentCallId}
            show={showActionPanel}
            setShow={setShowActionPanel}
          />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
